"use client";
import CurrencyDisplay from "@/app/components/currencyDisplay";
import ProductImage from "@/app/ec/product/productImage";
import { DeviceType } from "@/app/search/baseSearchPageResults";
import AddToCart from "@/app/search/products/addToCart";
import LastPurchased from "@/components/last-purchased/lastPurchased";
import Packaging from "@/components/products-tile/packaging";
import StarRatingOrPlaceholder from "@/components/products-tile/starRatingOrPlaceholder";
import { ViewMode } from "@/components/view-toggle/viewToggle";
import { SearchTypes } from "@/declarations/search";
import { useAppSelector } from "@/store/hooks";
import { makeGetBestPriceByMpIdSelector } from "@/store/searchSlice";
import { initializeAlgoliaAnalytics } from "@/utils/algoliaAnalytics";
import fixURI from "@/utils/fixURI";
import Big from "big.js";
import Image from "next/image";
import { useCallback, useMemo } from "react";
import aa from "search-insights";
import style from "./productsCard.module.scss";

export default function ProductCard({
  viewMode,
  mp_id,
  title,
  link,
  brand,
  price,
  retail_price,
  attributes,
  number_of_general_ratings,
  image_link,
  general_rating,
  pageType = "search",
  buyGetBaseURI,
  fast_delivery_regions,
  free_shipping_regions,
  category,
  disableAddToCart = false,
  additionalDetailsFromServer,
  queryId,
  position,
  shippingRegionId = 0,
  sponsoredProduct,
  deviceType,
  indexName,
  userToken,
}: ProductCardProps) {
  const selectBestPriceByMpId = useMemo(makeGetBestPriceByMpIdSelector, []);
  const productFromServer = useAppSelector((state) => selectBestPriceByMpId(state, mp_id));
  const algoliaAppId = useAppSelector((state) => state.config.algoliaAppId);
  const algoliaSearchApiKey = useAppSelector((state) => state.config.algoliaSearchApiKey);

  const vendorProductId = useMemo(() => {
    if (sponsoredProduct) {
      return additionalDetailsFromServer?.vendorProductId || 0;
    } else if (!productFromServer) {
      return 0;
    } else return productFromServer.vendorProductId;
  }, [sponsoredProduct, additionalDetailsFromServer, productFromServer]);

  const unitPrice = sponsoredProduct ? price : productFromServer?.unitPrice || price;

  const retailPrice = useMemo(() => {
    if (!productFromServer) {
      if (new Big(price).gte(retail_price)) {
        return 0;
      } else {
        return retail_price;
      }
    } else {
      if (!productFromServer.retailPrice) {
        if (new Big(price).gte(retail_price)) {
          return 0;
        } else {
          return retail_price;
        }
      } else if (new Big(productFromServer.unitPrice).gte(productFromServer.retailPrice)) {
        return 0;
      } else {
        return productFromServer.retailPrice;
      }
    }
  }, [productFromServer, price, retail_price]);

  const minQty = useMemo(() => {
    if (sponsoredProduct) {
      return additionalDetailsFromServer?.minQty || 1;
    } else if (!productFromServer) {
      return 1;
    }
    return productFromServer.minQty;
  }, [sponsoredProduct, additionalDetailsFromServer, productFromServer]);

  const inventory = useMemo(() => {
    if (sponsoredProduct) {
      return additionalDetailsFromServer?.inventory || 0;
    } else if (!productFromServer) {
      return 0;
    }
    return productFromServer.inventory;
  }, [sponsoredProduct, additionalDetailsFromServer, productFromServer]);

  const isMobile = deviceType === "mobile";

  const isListView = viewMode === "list" || deviceType !== "desktop";

  const productLink = useMemo(() => {
    let itemLink = fixURI(link);
    const params: string[] = [];

    if (queryId && !sponsoredProduct) {
      params.push(`queryID=${queryId}`);
    }

    if (sponsoredProduct) {
      params.push(`tsid=${sponsoredProduct.vendorId}`);
    }
    if (params.length > 0) {
      itemLink += "?" + params.join("&");
    }
    return itemLink;
  }, [link, queryId, sponsoredProduct]);

  const sponsoredAttributes = useMemo(() => {
    if (sponsoredProduct) {
      return {
        "data-ts-resolved-bid": sponsoredProduct.resolvedBidId,
        "data-ts-product": mp_id,
        auction: sponsoredProduct.auctionType ?? "Automatic",
        position: sponsoredProduct.position ?? position,
      };
    }
  }, [sponsoredProduct, mp_id, position]);

  const algoliaAttributes = useMemo(() => {
    if (sponsoredProduct) {
      return {};
    }
    return {
      "data-query-id": queryId,
      "data-click-position": position,
    };
  }, [sponsoredProduct, queryId, position]);

  const isFreeShippingEligible = free_shipping_regions?.includes(shippingRegionId) || false;
  const isFastDeliveryEligible = fast_delivery_regions?.includes(shippingRegionId) || false;

  // Create hit object for Algolia analytics
  const hit = useMemo(
    () => ({
      objectID: mp_id.toString(),
      __position: position,
      __queryID: queryId,
    }),
    [position, queryId, mp_id],
  );

  const priceRow = (
    <div className={style.priceRow}>
      <span className={style.price}>
        <CurrencyDisplay value={unitPrice} />
      </span>
      {retailPrice > 0 && (
        <span className={style.retailPrice}>
          <CurrencyDisplay value={retailPrice} />
        </span>
      )}
    </div>
  );

  const handleProductClick = useCallback(
    (hit: any) => {
      if (!initializeAlgoliaAnalytics(algoliaAppId, algoliaSearchApiKey)) {
        console.warn("Algolia analytics initialization failed, skipping event");
        return;
      }
      try {
        const eventData: any = {
          eventName: "Product Clicked",
          index: indexName,
          objectIDs: [hit.objectID],
          positions: [hit.__position],
          ...(userToken ? { userToken } : {}),
        };

        // Only add queryID if it exists
        if (hit.__queryID) {
          eventData.queryID = hit.__queryID;
        }
        // Use the correct event name for Algolia Insights
        aa("clickedObjectIDsAfterSearch", eventData);
      } catch (error) {
        console.error("Failed to send Algolia click event:", error);
      }
    },
    [algoliaAppId, algoliaSearchApiKey, indexName, userToken],
  );

  return (
    <div
      className={`${isListView ? style.listViewCard : style.gridView} ${style.customCard}`}
      {...algoliaAttributes}
      {...sponsoredAttributes}
    >
      {sponsoredProduct && !isListView && (
        <div className={style.gridSponsoredLabelContainer}>
          <span className={style.sponsoredLabel}>Sponsored</span>
        </div>
      )}

      <div className={style.topContent}>
        <a
          className={`product-search-link ${isListView ? style.imgContainerWithLabel : ""}`}
          href={productLink}
          onClick={() => {
            if (!sponsoredProduct) handleProductClick(hit);
          }}
          {...(sponsoredProduct ? { "data-ts-clickable": true } : {})}
        >
          <div className={`d-flex justify-content-center ${style.productImageContainer}`}>
            <ProductImage src={image_link} className={style.productImage} alt={title} width={160} height={160} />
          </div>
        </a>

        <div className={style.cardBody}>
          {sponsoredProduct && isListView && (
            <div className={style.sponsoredLabelContainer}>
              <span className={style.sponsoredLabel}>Sponsored</span>
            </div>
          )}

          <div className={style.textAndReviews}>
            <h5 className={style.productGridTitle}>{brand}</h5>
            <a
              href={productLink}
              onClick={() => {
                if (!sponsoredProduct) handleProductClick(hit);
              }}
              {...(sponsoredProduct ? { "data-ts-clickable": true } : {})}
              id="product-description"
              className={`product-search-link ${style.productDescription} ${isListView ? style.productListTitle : ""}`}
            >
              {title}
            </a>
            <Packaging packaging={attributes.packaging} />
            <StarRatingOrPlaceholder
              general_rating={general_rating}
              number_of_general_ratings={number_of_general_ratings}
            />
          </div>

          {isListView && priceRow}

          <div className={style.badges}>
            <div
              className={`${style.badgeLabel} ${style.badgeFreeShipping} freeShippingBadge`}
              title="This product is eligible for free shipping based on your delivery location."
            >
              {isFreeShippingEligible && (
                <>
                  <Image
                    className={style.badgeIcon}
                    src="/media/dental/common/icons/free_shipping_icon.svg"
                    height={13}
                    width={13}
                    alt="free shipping icon"
                  />
                  <p className="m-0">Free Shipping</p>
                </>
              )}
            </div>

            <div
              className={`${style.badgeLabel} ${style.badgeFastDelivery} fastDeliveryBadge`}
              title="Delivery within 4 business days or less."
            >
              {isFastDeliveryEligible && (
                <>
                  <Image
                    className={style.badgeIcon}
                    src="/media/dental/common/icons/fast_delivery_icon.svg"
                    height={13}
                    width={13}
                    alt="fast delivery Icon"
                  />
                  <p className="m-0">Fast Delivery</p>
                </>
              )}
            </div>
          </div>

          <LastPurchased
            date={productFromServer?.lastPurchaseDate}
            mpId={mp_id}
            slotClass={style.lastPurchased}
            hasContentClass={style.hasContent}
            isTopsortClickable={!!sponsoredProduct}
          />
        </div>
      </div>

      {(!isListView || !disableAddToCart) && (
        <div className={style.bottomContent}>
          {!isListView && priceRow}

          {disableAddToCart === false && (
            <AddToCart
              isListView={isListView}
              deviceType={deviceType}
              mp_id={mp_id}
              category={category}
              title={title}
              price={price}
              brand={brand}
              vendorProductId={vendorProductId}
              minQty={minQty}
              inventory={inventory}
              position={position}
              queryId={queryId}
              isSponsoredProduct={!!sponsoredProduct}
              resolvedBidId={sponsoredProduct?.resolvedBidId}
            />
          )}
        </div>
      )}
    </div>
  );
}

export type ProductCardProps = SearchTypes.ProductHit & {
  viewMode: ViewMode;
  pageType?: "search" | "category" | "buy-get";
  buyGetBaseURI?: string;
  disableAddToCart?: boolean;
  position: number;
  queryId?: string;
  shippingRegionId?: number;
  deviceType: DeviceType;
  indexName: string;
  userToken: string | undefined;
};
